import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { MessageSquare, Send } from "lucide-react";

export default function ChatDrawer({ chat, unread, onMarkRead, onSend, me }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (open) {
      onMarkRead?.();
      requestAnimationFrame(() => {
        if (scrollerRef.current) {
          scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
        }
      });
    }
  }, [open, chat.length, onMarkRead]);

  const submit = (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    onSend(t);
    setText("");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="fixed bottom-6 left-4 sm:left-6 z-30 w-14 h-14 rounded-full bg-pbo-green text-pbo-cream shadow-lg flex items-center justify-center hover:bg-pbo-green-deep transition-colors"
          aria-label="Open chat"
          data-testid="chat-toggle-btn"
        >
          <MessageSquare className="w-5 h-5" />
          {unread > 0 && (
            <span
              className="absolute -top-1 -right-1 bg-pbo-gold text-pbo-ink text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center font-mono"
              data-testid="chat-unread-badge"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-pbo-cream border-l border-pbo-border p-0 flex flex-col"
        data-testid="chat-drawer"
      >
        <SheetHeader className="px-5 py-4 border-b border-pbo-border">
          <SheetTitle className="font-serif text-pbo-green text-xl flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-pbo-gold" />
            Clubhouse chat
          </SheetTitle>
        </SheetHeader>
        <div
          ref={scrollerRef}
          className="flex-1 overflow-y-auto px-5 py-4 space-y-3 scrollbar-thin"
          data-testid="chat-messages"
        >
          {chat.length === 0 ? (
            <div className="text-sm text-pbo-muted italic text-center py-10">
              No messages yet. Talk some trash.
            </div>
          ) : (
            chat.map((m, i) => {
              const mine = m.from === me?.username;
              return (
                <div
                  key={`${m.ts}-${i}`}
                  className={`flex flex-col ${mine ? "items-end" : "items-start"}`}
                >
                  <div className="text-[10px] uppercase tracking-wider text-pbo-muted mb-0.5">
                    {mine ? "You" : m.from}
                  </div>
                  <div
                    className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm leading-snug ${
                      mine
                        ? "bg-pbo-green text-pbo-cream rounded-tr-sm"
                        : "bg-white border border-pbo-border text-pbo-ink rounded-tl-sm"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              );
            })
          )}
        </div>
        <form
          onSubmit={submit}
          className="border-t border-pbo-border p-3 flex items-center gap-2 bg-pbo-paper"
        >
          <Input
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 200))}
            placeholder="Say something to the group..."
            className="bg-white border-pbo-border"
            data-testid="chat-input"
          />
          <Button
            type="submit"
            disabled={!text.trim()}
            className="bg-pbo-gold text-pbo-ink hover:bg-pbo-gold-soft h-10 w-10 p-0 rounded-full"
            data-testid="chat-send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}
